import { ReactNode } from 'react';
import { Common } from '@thecvlb/design-system';
import classNames from 'classnames';

import { MessageEvent } from 'utils/enums';

import { MessageProps } from './message.types';
import AppointmentMessage from './AppointmentMessage';
import DefaultMessage from './DefaultMessage';
import File from './File';
import Status from './Status';
import SystemMessage from './SystemMessage';

import ProviderAvatar from 'assets/icons/doc-avatar.svg';

type Props = MessageProps & {
  /** Passed through to the default bubble only; see `DefaultMessage`. */
  actions?: ReactNode;
  /**
   * Rendered between the sender label and the bubble: the AI chat's support-task step, which
   * belongs to the assistant's turn and sits under its name, above the reply (Dominique, Sept 18).
   */
  beforeBubble?: ReactNode;
  /**
   * This message continues a run of the same sender (the one before it is theirs, minutes
   * apart). The label sits above the first bubble of a run and is not repeated here, while
   * `combined` (continued by the next one) keeps deciding where the time goes: under the last.
   */
  continuation?: boolean;
  /**
   * Replaces the avatar with a small label above the bubble: the AI chat's sparkle and assistant
   * name, or "You" for the patient's own line, the way the platform redesign presents its chat
   * (Dominique, Sept 17). Shown on the first message of a run, like the avatar. The care-team chat
   * passes nothing and keeps its avatars.
   */
  senderLabel?: ReactNode;
};

const Message = ({
  actions,
  beforeBubble,
  combined,
  continuation = false,
  message,
  mobile,
  self,
  senderLabel
}: Props) => {
  const labelled = senderLabel !== undefined;

  return (
    <div
      className={classNames('md:px-4', {
        'mb-1': combined
      })}
      data-testid="message"
    >
      <div
        className={classNames(
          'flex h-fit items-end',
          {
            'flex-row-reverse': self
          },
          !self && combined && !labelled && (mobile ? 'ml-14' : 'ml-12')
        )}
      >
        {!combined && !self && !labelled && (
          <Common.ProfileImage
            className="mb-[26px]"
            size="lg"
            src={message.senderDetails?.profileImage || ProviderAvatar}
          />
        )}
        <div
          className={classNames('flex flex-col gap-3', {
            'items-end': self,
            'ml-3': !mobile && !self && !labelled
          })}
        >
          {labelled && !continuation && (
            <div
              className="-mb-1 flex items-center gap-2 text-base font-semibold text-gray-800"
              data-testid="message_sender_label"
            >
              {senderLabel}
            </div>
          )}
          {beforeBubble}
          <File combined={combined} message={message} mobile={mobile} self={self} />
          {message.event &&
          [MessageEvent.AppointmentCreated, MessageEvent.AppointmentCancelled].includes(
            message.event
          ) ? (
            <AppointmentMessage combined={combined} message={message} self={self} />
          ) : message.event &&
            [MessageEvent.AppointmentMissed, MessageEvent.AppointmentComplete].includes(
              message.event
            ) ? (
            <SystemMessage combined={combined} message={message} self={self} />
          ) : (
            <DefaultMessage actions={actions} combined={combined} message={message} self={self} />
          )}

          <Status
            combined={combined}
            hideSenderName={labelled}
            message={message}
            mobile={mobile}
            self={self}
          />
        </div>
      </div>
    </div>
  );
};

export default Message;
